import React, { useId } from "react";

// Format glyph: a sheet of paper drawn at the format's real proportions inside the
// 60×48 frame the design system uses ("Format Icon", Oppizi Component Library).
// Tearaway / Door Hanger are the master geometry exported from that component set,
// centred in the same frame. Every colour binds to the `--sds-*` tokens, so the
// glyphs follow light/dark like the rest of the shadcn surfaces.

interface FormatIconProps {
  /** Format width (any consistent unit — used for aspect ratio + size scale). */
  formatWidth: number;
  /** Format height (any consistent unit — used for aspect ratio + size scale). */
  formatHeight: number;
  orientation: "landscape" | "portrait";
  /** Square container size in pixels. */
  containerSize?: number;
  /** Size name for custom-icon exceptions (e.g. "Tearaway", "Door hanger"). */
  sizeName?: string;
}

/** Emphasizes aspect-ratio differences between formats (1.0 = none, higher = more). */
const SIZE_EMPHASIS_FACTOR = 1.4;
/** How much physical size scales the rectangle (0 = uniform, 1 = full). */
const SIZE_SCALE_FACTOR = 0.8;
/** Reference perimeter (inches) used to normalize size scaling. */
const REFERENCE_PERIMETER = 15;
/** Breathing room between the paper and the frame edge, per side. */
const FRAME_PADDING = 8;
/** Paper margin around the inner fill (matches the design-system paper). */
const INNER_PADDING = 2;
const BORDER_WIDTH = 0.5;

/** Frame the design system draws every format glyph in. */
const GLYPH_WIDTH = 60;
const GLYPH_HEIGHT = 48;

const PAPER_FILL = { fill: "hsl(var(--sds-background))" };
const INNER_FILL = { fill: "hsl(var(--sds-border))" };
const EDGE = { stroke: "hsl(var(--sds-ring))" };
const EDGE_FILL = { fill: "hsl(var(--sds-ring))" };
const PERFORATION = { stroke: "hsl(var(--sds-muted-foreground))" };

interface PaperGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  innerX: number;
  innerY: number;
  innerWidth: number;
  innerHeight: number;
  innerRadius: number;
}

const paperGeometry = (
  displayWidth: number,
  displayHeight: number,
  frameWidth: number,
  frameHeight: number
): PaperGeometry => {
  const aspectRatio = displayWidth / displayHeight;

  // Larger formats get larger sheets within the frame.
  const perimeter = displayWidth + displayHeight;
  const sizeScale = 1 + (perimeter / REFERENCE_PERIMETER - 1) * SIZE_SCALE_FACTOR;
  const maxSize = (frameHeight - FRAME_PADDING * 2) * Math.min(sizeScale, 1.2); // cap at 20% larger

  let width: number;
  let height: number;
  if (aspectRatio > 1) {
    width = maxSize;
    const emphasisAdjustment = Math.pow(aspectRatio, SIZE_EMPHASIS_FACTOR - 1);
    height = maxSize / (aspectRatio * emphasisAdjustment);
  } else {
    height = maxSize;
    const emphasisAdjustment = Math.pow(1 / aspectRatio, SIZE_EMPHASIS_FACTOR - 1);
    width = maxSize * (aspectRatio / emphasisAdjustment);
  }

  const radius = Math.min(width, height) * 0.1;

  return {
    x: (frameWidth - width) / 2,
    y: (frameHeight - height) / 2,
    width,
    height,
    radius,
    innerX: (frameWidth - width) / 2 + INNER_PADDING,
    innerY: (frameHeight - height) / 2 + INNER_PADDING,
    innerWidth: width - INNER_PADDING * 2,
    innerHeight: height - INNER_PADDING * 2,
    innerRadius: radius * 0.75,
  };
};

const Paper = ({
  geometry,
  clipId,
  children,
}: {
  geometry: PaperGeometry;
  clipId: string;
  children?: React.ReactNode;
}) => (
  <>
    <defs>
      <clipPath id={clipId}>
        <rect x={geometry.x} y={geometry.y} width={geometry.width} height={geometry.height} rx={geometry.radius} />
      </clipPath>
    </defs>
    <rect
      x={geometry.x}
      y={geometry.y}
      width={geometry.width}
      height={geometry.height}
      rx={geometry.radius}
      style={PAPER_FILL}
    />
    <rect
      x={geometry.innerX}
      y={geometry.innerY}
      width={geometry.innerWidth}
      height={geometry.innerHeight}
      rx={geometry.innerRadius}
      style={INNER_FILL}
      clipPath={`url(#${clipId})`}
    />
    {children}
    <rect
      x={geometry.x + BORDER_WIDTH / 2}
      y={geometry.y + BORDER_WIDTH / 2}
      width={geometry.width - BORDER_WIDTH}
      height={geometry.height - BORDER_WIDTH}
      rx={geometry.radius}
      style={EDGE}
      strokeWidth={BORDER_WIDTH}
      fill="none"
    />
  </>
);

/** Wraps a master glyph in the design-system 60×48 frame. */
const GlyphFrame = ({ children }: { children: React.ReactNode }) => (
  <svg
    width={GLYPH_WIDTH}
    height={GLYPH_HEIGHT}
    viewBox={`0 0 ${GLYPH_WIDTH} ${GLYPH_HEIGHT}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

/** Custom icon component for Tearaway format (master: 18 × 34.8, centred). */
export const TearawayIcon = () => (
  <GlyphFrame>
    <g transform="translate(21 6.6)">
      <rect x="0.20339" y="0.20339" width="17.5932" height="34.3932" rx="3.79661" style={PAPER_FILL} />
      <rect
        x="0.20339"
        y="0.20339"
        width="17.5932"
        height="34.3932"
        rx="3.79661"
        fill="none"
        style={EDGE}
        strokeWidth={0.40678}
      />
      <rect width="30.8" height="14" rx="2" transform="matrix(0 1 1 0 2 2)" style={INNER_FILL} />
      <path d="M16 19.4L2 19.4" fill="none" style={PERFORATION} strokeWidth={0.5} strokeDasharray="1 1" />
    </g>
  </GlyphFrame>
);

const DOOR_HANGER_DIECUT =
  "M13.3594 0C14.4639 0 15.3594 0.895431 15.3594 2V31.5576C15.3594 32.6622 14.4639 33.5576 13.3594 33.5576H2C0.895431 33.5576 0 32.6622 0 31.5576V6.98047C0.674157 6.36528 1.33594 5.8125 1.33594 5.8125C1.32735 5.80731 0.640832 5.39072 0 4.63379V2C1.32501e-07 0.895431 0.895431 0 2 0H13.3594ZM8.06055 3.72852C6.47808 3.72875 6.09693 5.31045 5.27637 5.7793C6.15557 6.21881 6.53667 7.82983 8.06055 7.83008C9.19376 7.83008 10.1123 6.91188 10.1123 5.7793C10.1122 4.64678 9.19371 3.72852 8.06055 3.72852Z";

/** Custom icon component for Doorhanger format (master: 15.3594 × 33.5576, centred). */
export const DoorhangerIcon = () => {
  const maskId = useId();
  return (
    <GlyphFrame>
      <g transform="translate(22.3203 7.2212)">
        <mask id={maskId} fill="white">
          <path d={DOOR_HANGER_DIECUT} />
        </mask>
        <path d={DOOR_HANGER_DIECUT} style={PAPER_FILL} />
        <path
          d="M2 33.5576V34.0576V34.0576V33.5576ZM0 6.98047L-0.337032 6.61113L-0.5 6.75985V6.98047H0ZM1.33594 5.8125L1.65647 6.19624L2.19396 5.74729L1.59479 5.38472L1.33594 5.8125ZM0 4.63379H-0.5V4.81702L-0.381606 4.95686L0 4.63379ZM0 2L-0.5 2V2H0ZM8.06055 3.72852V3.22852H8.06047L8.06055 3.72852ZM5.27637 5.7793L5.02832 5.34516L4.21701 5.80872L5.0528 6.22653L5.27637 5.7793ZM8.06055 7.83008L8.06047 8.33008H8.06055V7.83008ZM10.1123 5.7793L10.6123 5.7793V5.77925L10.1123 5.7793ZM13.3594 0V0.5C14.1878 0.5 14.8594 1.17157 14.8594 2H15.3594H15.8594C15.8594 0.619288 14.7401 -0.5 13.3594 -0.5V0ZM15.3594 2H14.8594V31.5576H15.3594H15.8594V2H15.3594ZM15.3594 31.5576H14.8594C14.8594 32.386 14.1878 33.0576 13.3594 33.0576V33.5576V34.0576C14.7401 34.0576 15.8594 32.9383 15.8594 31.5576H15.3594ZM13.3594 33.5576V33.0576H2V33.5576V34.0576H13.3594V33.5576ZM2 33.5576V33.0576C1.17157 33.0576 0.5 32.386 0.5 31.5576H0H-0.5C-0.5 32.9383 0.619288 34.0576 2 34.0576V33.5576ZM0 31.5576H0.5V6.98047H0H-0.5V31.5576H0ZM0 6.98047L0.337032 7.3498C0.669418 7.04649 0.99956 6.75784 1.24709 6.54463C1.37072 6.43814 1.4734 6.35076 1.54505 6.29011C1.58086 6.25979 1.6089 6.23617 1.6279 6.22021C1.6374 6.21222 1.64463 6.20616 1.64945 6.20212C1.65186 6.2001 1.65366 6.1986 1.65483 6.19761C1.65542 6.19712 1.65586 6.19676 1.65613 6.19653C1.65627 6.19641 1.65636 6.19633 1.65642 6.19628C1.65645 6.19626 1.65647 6.19625 1.65648 6.19624C1.65648 6.19623 1.65648 6.19624 1.65648 6.19623C1.65648 6.19624 1.65647 6.19624 1.33594 5.8125C1.0154 5.42876 1.01539 5.42877 1.01538 5.42878C1.01537 5.42879 1.01535 5.4288 1.01534 5.42881C1.01531 5.42884 1.01526 5.42887 1.01521 5.42892C1.01511 5.429 1.01496 5.42912 1.01478 5.42928C1.01441 5.42959 1.01389 5.43003 1.0132 5.4306C1.01183 5.43174 1.00984 5.43341 1.00723 5.4356C1.00203 5.43996 0.994385 5.44637 0.984471 5.4547C0.964642 5.47137 0.935713 5.49574 0.898953 5.52686C0.825446 5.58908 0.720555 5.67834 0.594463 5.78695C0.342566 6.00392 0.00473924 6.29925 -0.337032 6.61113L0 6.98047ZM1.33594 5.8125L1.59479 5.38472C1.60776 5.39257 0.972686 5.00888 0.381606 4.31071L0 4.63379L-0.381606 4.95686C0.308978 5.77256 1.04694 6.22204 1.07708 6.24028L1.33594 5.8125ZM0 4.63379H0.5V2H0H-0.5V4.63379H0ZM0 2H0.5C0.5 1.17157 1.17157 0.5 2 0.5V0V-0.5C0.619288 -0.5 -0.5 0.619288 -0.5 2L0 2ZM2 0V0.5H13.3594V0V-0.5H2V0ZM8.06055 3.72852L8.06047 3.22852C7.06303 3.22866 6.44649 3.74394 6.00578 4.24969C5.81047 4.47383 5.58876 4.77308 5.44857 4.94386C5.28002 5.14919 5.15012 5.27557 5.02832 5.34516L5.27637 5.7793L5.52442 6.21343C5.8129 6.0486 6.03835 5.80146 6.2215 5.57835C6.43301 5.32068 6.55934 5.13659 6.7597 4.90666C7.11033 4.50429 7.4756 4.2286 8.06062 4.22852L8.06055 3.72852ZM5.27637 5.7793L5.0528 6.22653C5.18166 6.29095 5.31774 6.41223 5.48904 6.61127C5.57296 6.70878 5.65671 6.81439 5.75037 6.93228C5.84178 7.04733 5.94177 7.173 6.04844 7.29639C6.47983 7.79545 7.08229 8.32992 8.06047 8.33008L8.06055 7.83008L8.06063 7.33008C7.51493 7.32999 7.1649 7.05883 6.80497 6.64244C6.71381 6.53697 6.62604 6.42691 6.53336 6.31024C6.44293 6.19641 6.34625 6.0743 6.24699 5.95896C6.05193 5.73231 5.81068 5.4874 5.49994 5.33207L5.27637 5.7793ZM8.06055 7.83008V8.33008C9.4697 8.33008 10.6123 7.18823 10.6123 5.7793L10.1123 5.7793L9.6123 5.77929C9.61229 6.63553 8.91782 7.33008 8.06055 7.33008V7.83008ZM10.1123 5.7793L10.6123 5.77925C10.6122 4.37046 9.46968 3.22852 8.06055 3.22852V3.72852V4.22852C8.91773 4.22852 9.61223 4.9231 9.6123 5.77934L10.1123 5.7793Z"
          style={EDGE_FILL}
          mask={`url(#${maskId})`}
        />
        <path
          d="M12.8041 1.47757C13.3564 1.47757 13.8041 1.92528 13.8041 2.47757V30.8819C13.8041 31.4341 13.3564 31.8819 12.8041 31.8819H2.50334C1.95106 31.8819 1.50334 31.4341 1.50334 30.8819V7.30667C2.55799 6.38238 3.35686 5.71487 3.35686 5.71487C3.35686 5.71487 2.53948 5.2222 1.50334 4.29495V2.47757C1.50334 1.92528 1.95106 1.47757 2.50334 1.47757H12.8041ZM8.01995 3.2803C6.09071 3.2803 5.62564 5.20771 4.62541 5.77932C5.69709 6.31523 6.16215 8.27932 8.01995 8.27932C9.40107 8.2791 10.5209 7.15973 10.5209 5.77932C10.5207 4.39905 9.40097 3.28052 8.01995 3.2803Z"
          style={INNER_FILL}
        />
      </g>
    </GlyphFrame>
  );
};

const CUSTOM_ICONS: Record<string, React.ComponentType> = {
  Tearaway: TearawayIcon,
  Doorhanger: DoorhangerIcon,
};

const FormatIcon = ({ formatWidth, formatHeight, orientation, containerSize = 56, sizeName }: FormatIconProps) => {
  // Unique per instance so the clipPath id never collides when the same format renders
  // twice (e.g. the dropdown trigger + its matching option).
  const clipId = useId();

  // Custom-glyph exception (Tearaway / Door Hanger). Normalize spaces ("Door hanger"
  // → "Doorhanger"). Both are drawn at their native 60×48 frame, which is exactly the
  // slot the format row gives them.
  if (sizeName) {
    const CustomIcon = CUSTOM_ICONS[sizeName.replace(/\s+/g, "")];
    if (CustomIcon) return <CustomIcon />;
  }

  // Swap dimensions when displaying landscape.
  const displayWidth = orientation === "landscape" ? formatHeight : formatWidth;
  const displayHeight = orientation === "landscape" ? formatWidth : formatHeight;

  const geometry = paperGeometry(displayWidth, displayHeight, containerSize, containerSize);

  return (
    <svg width={containerSize} height={containerSize} viewBox={`0 0 ${containerSize} ${containerSize}`} fill="none">
      <Paper geometry={geometry} clipId={clipId} />
    </svg>
  );
};

export default FormatIcon;
